import { useEffect, useState, useRef, useMemo } from 'react';
import { Container, Row, Col, Button, Modal, Form, Image } from 'react-bootstrap';
import { Header } from '../../features/mobile_header/Header';
import { useDispatch, useSelector } from 'react-redux';
import { selectDevice } from '../../features/devices/deviceSlice';
import { cancelDeviceSubsriptionThunk, deleteDeviceThunk, deviceSubscribeThunk, getDeviceDetailThunk, regenerateClaimCodeThunk, updateDeviceThunk } from '../../features/devices/deviceThunks';
import { useAuth } from '../../providers/AuthProvider';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from "react-router-dom";
import Select from 'react-select';
import useDidMountHook from '../../hooks/useDidMountHook';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from '../../components/CheckoutForm';
import { selectCurrencySymbolSlice } from '../../features/currencysymbol/currencysymbolSlice';
import { currencysymbolListThunk } from '../../features/currencysymbol/currencysymbolThunks';
import { getPairingState, getLivenessState, relativeTime, PAIRING, LIVENESS } from '../../utils/deviceStatus';
import { buildInstrumentGroups, toSelectOptions, findInstrumentOption, findInstrumentOptions, isGrouped, marketChoices, optionsForMarket, defaultMarket } from '../../utils/instrumentOptions';

const ALL_MARKETS = 'all';

// Sticky group headings in the instrument pickers: each market heading stays at
// the top of the list while its own group is on screen, and the next heading
// pushes it out of the way. A sticky element needs an opaque background of its
// own, or the options scroll through it.
const instrumentSelectStyles = {
	group: (base) => ({ ...base, paddingTop: 0 }),
	groupHeading: (base, state) => ({
		...base,
		position: 'sticky',
		top: 0,
		zIndex: 2,
		margin: 0,
		paddingTop: 8,
		paddingBottom: 8,
		background: (state.theme && state.theme.colors && state.theme.colors.neutral0) || '#fff',
		// A hairline under the heading, drawn inside so it cannot shift the layout.
		boxShadow: 'inset 0 -1px 0 rgba(0, 0, 0, 0.08)',
	}),
};

export default function UpdateDevice({props}) {
	
    const { REACT_APP_PAYMENT_PKEY } = process.env;
    const stripePromise = loadStripe(REACT_APP_PAYMENT_PKEY);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	let { device_id } = useParams();

	var [user] = useAuth();
    const toastId = useRef();
    const [toastGet, toastSet] = useState(toastId.current);

	const { status, event, message, data, claim, claimStatus, claimMessage } = useSelector(selectDevice);
	const { status: symbolStatus, data: symbolData } = useSelector(selectCurrencySymbolSlice);

	// ═══════════════════════════════════════════════════════════════════════
	// SCREEN CONFIGURATION
	// ═══════════════════════════════════════════════════════════════════════

	const available_screens = [
		{ value: '1', src: require('../../assets/images/screens/screen-1.png'), name: 'Screen 1' },
		{ value: '2', src: require('../../assets/images/screens/screen-2.png'), name: 'Screen 2' },
		{ value: '3', src: require('../../assets/images/screens/screen-3.png'), name: 'Screen 3' },
		{ value: '4', src: require('../../assets/images/screens/screen-4.png'), name: 'Screen 4' },
		{ value: '5', src: require('../../assets/images/screens/screen-5.png'), name: 'Screen 5' },
		{ value: '6', src: require('../../assets/images/screens/screen-6.png'), name: 'Screen 6' },
		{ value: '7', src: require('../../assets/images/screens/screen-7.png'), name: 'Screen 7' },
		{ value: '8', src: require('../../assets/images/screens/screen-8.png'), name: 'Screen 8' },
	];

	// ═══════════════════════════════════════════════════════════════════════
	// STATE
	// ═══════════════════════════════════════════════════════════════════════

	const [selectedDevice, setSelectedDevice] = useState({});
	const [showCancelSubscription, setShowCancelSubscription] = useState(false);
	const [showUpgradeSubscription, setShowUpgradeSubscription] = useState(false);
	const [showClaimModal, setShowClaimModal] = useState(false);
	const [claimCopied, setClaimCopied] = useState(false);
	// Delete-device confirmation requires the user to retype the device_id, so a
	// stray click on the danger button can't actually destroy the device.
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteConfirmText, setDeleteConfirmText] = useState('');
	const [deletePending, setDeletePending] = useState(false);

	const handleClose = () => setShowCancelSubscription(false);
	const handleShow = () => setShowCancelSubscription(true);
	const handleUpgradeClose = () => setShowUpgradeSubscription(false);
	const handleUpgradeShow = () => setShowUpgradeSubscription(true);

	const requestClaimCode = () => {
		if (!selectedDevice?.device_id) return;
		setShowClaimModal(true);
		setClaimCopied(false);
		dispatch(regenerateClaimCodeThunk({ device_id: selectedDevice.device_id }));
	};

	const closeClaimModal = () => {
		setShowClaimModal(false);
		setClaimCopied(false);
	};

	const copyClaim = async () => {
		if (!claim?.code) return;
		try {
			await navigator.clipboard.writeText(claim.code);
			setClaimCopied(true);
			setTimeout(() => setClaimCopied(false), 2000);
		} catch (e) {
			toast.info('Copy unavailable — write the code down manually.');
		}
	};

	const formatExpiry = (iso) => {
		if (!iso) return '';
		try {
			const minutes = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 60000));
			return `Expires in ${minutes} min`;
		} catch (e) { return ''; }
	};

	useEffect(() => {
		if (claimStatus === 'error') {
			toast.error(claimMessage || 'Could not generate pairing code');
			setShowClaimModal(false);
		}
	}, [claimStatus, claimMessage]);

	const openDeleteModal = () => {
		setDeleteConfirmText('');
		setShowDeleteModal(true);
	};
	const closeDeleteModal = () => {
		if (deletePending) return; // Don't let user dismiss while the API call is in flight.
		setShowDeleteModal(false);
		setDeleteConfirmText('');
	};
	const confirmDelete = async () => {
		if (!selectedDevice?.device_id) return;
		if (deleteConfirmText !== selectedDevice.device_id) return;
		setDeletePending(true);
		await dispatch(deleteDeviceThunk({ device_id: selectedDevice.device_id }));
	};

	useEffect(() => {
		if (event !== 'delete') return;
		if (status === 'delete_success') {
			setDeletePending(false);
			setShowDeleteModal(false);
			toast.success('Device deleted', {
				position: "top-center", autoClose: 4000, hideProgressBar: true, theme: "light",
			});
			navigate('/devices');
		} else if (status === 'error') {
			setDeletePending(false);
			toast.error(message || 'Could not delete device');
		}
	}, [event, status, message, navigate]);

	// ═══════════════════════════════════════════════════════════════════════
	// HELPER FUNCTIONS
	// ═══════════════════════════════════════════════════════════════════════

	const isChartScreen = (screen) => {
		return ['5', '6', '7', '8'].includes(screen);
	};

	const isMultiSymbolMode = (device) => {
		return device?.screen === '4';
	};

	// ═══════════════════════════════════════════════════════════════════════
	// LIFECYCLE
	// ═══════════════════════════════════════════════════════════════════════

	useDidMountHook(async () => {
		if (device_id) {
			await dispatch(getDeviceDetailThunk({ device_id: device_id }));
		}
		dispatch(currencysymbolListThunk());
	});

	useEffect(() => {
    if (event === 'update') {
        if (status === "update_success") {
            toast.success('Successfully updated device', {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
            });
            navigate('/devices');
        }
    }

    if (event === 'detail_device') {
        if (status === "loaded") {
            if (data && data.length > 0) {
                const deviceInfo = JSON.parse(JSON.stringify(data[0]));

                deviceInfo.screenVersion = 'v2';

                if (!deviceInfo.symbols && deviceInfo.screen === '4') {
                    deviceInfo.symbols = deviceInfo.cryptoCurrency?.map(c => c + 'USDT')
                        || ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];
                }

                if (!deviceInfo.symbol) {
                    const crypto = Array.isArray(deviceInfo.cryptoCurrency)
                        ? deviceInfo.cryptoCurrency?.[0]
                        : deviceInfo.cryptoCurrency;
                    deviceInfo.symbol = crypto ? crypto + 'USDT' : 'BTCUSDT';
                }

                setSelectedDevice(deviceInfo);
            } else {
                setSelectedDevice({});
            }
        }
    }

}, [status, event]);

	// ═══════════════════════════════════════════════════════════════════════
	// FORM HANDLERS
	// ═══════════════════════════════════════════════════════════════════════

	const submitUpdateForm = () => {
		if (selectedDevice?.screen === "4") {
			if (!selectedDevice.symbols || selectedDevice.symbols.length !== 4) {
				toast.warning('Please select exactly 4 symbols for Screen 4', {
					position: "top-center",
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: "light",
				});
				return;
			}
		}

		dispatch(updateDeviceThunk(selectedDevice));
	};

	const setScreenValue = (screen_id) => {
		const newDevice = { ...selectedDevice, screen: screen_id, screenVersion: 'v2' };

		newDevice.symbol = selectedDevice.symbol || 'BTCUSDT';

		if (screen_id === '4' && !newDevice.symbols) {
			newDevice.symbols = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];
		}

		setSelectedDevice(newDevice);
	};

	// What the pickers offer: the instruments of every market an admin has switched
	// on, grouped by market. One market stays a flat list with no heading.
	const instrumentGroups = useMemo(() => buildInstrumentGroups(symbolData), [symbolData]);
	const marketOptions = useMemo(() => marketChoices(instrumentGroups), [instrumentGroups]);
	// Null until the user picks one, so the selector opens on whatever market the
	// device is already set to instead of resetting it on every load.
	const [chosenMarket, setChosenMarket] = useState(null);
	const activeMarket = chosenMarket || defaultMarket(instrumentGroups, selectedDevice?.symbol);
	// One market at a time keeps the list short. ALL_MARKETS falls back to the
	// grouped list, which is still useful for hunting across markets.
	const instrumentOptions = useMemo(() => (
		activeMarket === ALL_MARKETS
			? toSelectOptions(instrumentGroups)
			: optionsForMarket(instrumentGroups, activeMarket)
	), [instrumentGroups, activeMarket]);
	const selectedInstrument = findInstrumentOption(instrumentGroups, selectedDevice?.symbol);
	// A device can hold an instrument that is no longer offered, because switching a
	// market off hides it from the pickers without changing what a panel displays.
	const instrumentMissing = Boolean(selectedDevice?.symbol) && !selectedInstrument;

	const selectSymbol = (symbol) => {
		setSelectedDevice({ ...selectedDevice, symbol: symbol.value });
	};

	const selectMultipleSymbols = (symbols) => {
		if (symbols && symbols.length > 0) {
			setSelectedDevice({ 
				...selectedDevice, 
				symbols: symbols.map(s => s.value) 
			});
		}
	};

	const submitSubscribe = async (pm) => {
		await dispatch(deviceSubscribeThunk({
			email: user?.email, 
			deviceId: selectedDevice?.device_id, 
			payment_method: pm?.paymentMethod?.id
		}));
	};

	const cancelSubscription = async () => {
		await dispatch(cancelDeviceSubsriptionThunk({
			subscriptionId: selectedDevice?.subscriptionId
		}));
	};

	// ═══════════════════════════════════════════════════════════════════════
	// RENDER
	// ═══════════════════════════════════════════════════════════════════════

	return (
		<>
			<Header page={'update-device'} />
			
			{(() => {
				if (status === "loaded" && selectedDevice && selectedDevice.name != undefined) {
					return (
						<>
							<Container fluid className="element-page update-device h-100 pt-5 pb-5">
								{/* Device Name & ID */}
								<Row>
									<Col md={12}>
										Update Your Device
									</Col>
								</Row>
								{(() => {
									const pair = getPairingState(selectedDevice);
									const live = getLivenessState(selectedDevice);
									return (
										<Row className='mb-3'>
											<Col md={12}>
												<div style={{
													padding: '14px 18px',
													background: 'rgba(255,255,255,0.04)',
													borderRadius: 12,
													border: '1px solid #374151',
													display: 'flex',
													flexWrap: 'wrap',
													gap: 18,
													alignItems: 'center',
												}}>
												<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
													<span style={{ width: 10, height: 10, borderRadius: '50%', background: pair.color }} />
													<div>
														<div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pairing</div>
														<div style={{ color: pair.color, fontWeight: 600 }}>{pair.label}</div>
														{pair.kind === PAIRING.PAIRED && pair.claimedAt && (
															<div style={{ fontSize: 11, color: '#9CA3AF' }}>Claimed {relativeTime(pair.claimedAt)}</div>
														)}
													</div>
												</div>
												<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
													<span style={{ width: 10, height: 10, borderRadius: '50%', background: live.color }} />
													<div>
														<div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>Liveness</div>
														<div style={{ color: live.color, fontWeight: 600 }}>{live.label}</div>
														{live.lastSeenAt && live.kind !== LIVENESS.ONLINE && (
															<div style={{ fontSize: 11, color: '#9CA3AF' }}>Last seen {relativeTime(live.lastSeenAt)}</div>
														)}
													</div>
												</div>
											</div>
											</Col>
										</Row>
									);
								})()}
								<Row>
									<Col md={6} sm={12}>
										<Form.Label>Device Name</Form.Label>
										<Form.Control 
											type="text" 
											name="name" 
											value={selectedDevice.name} 
											onChange={(e) => setSelectedDevice({ ...selectedDevice, name: e.target.value })} 
										/>
									</Col>
									<Col md={6} sm={12}>
										<Form.Label>Device ID</Form.Label>
										<Form.Control 
											type="text" 
											name="device_id" 
											disabled={true} 
											readOnly={true} 
											value={selectedDevice.device_id} 
										/>
										<Button
											size="sm"
											variant="outline-light"
											className="mt-2"
											onClick={requestClaimCode}
										>
											Get pairing code
										</Button>
									</Col>
								</Row>

								{/* Screen Selection */}
								<Row>
									<Col md={12} sm={12}>
										<Form.Label>Screen</Form.Label>
									</Col>
									<Row className="justify-content-center">
										<Col xl={1} className="d-none d-xl-block"></Col>
										{available_screens.map((screen, i) => (
											<Col xl={2} lg={4} md={6} sm={12} key={i}>
												<Form.Group
													onClick={() => setScreenValue(screen.value)}
													className={`form-group ${selectedDevice.screen === screen.value ? 'selected' : ''}`}
												>
													<Image src={screen.src} />
													<br />
													<Form.Label>{screen.name}</Form.Label>
												</Form.Group>
											</Col>
										))}
										<Col xl={1} className="d-none d-xl-block"></Col>
									</Row>
								</Row>

								{/* ═══════════════════════════════════════════════════════════════
								    SYMBOL SELECTION (Binance)
								    ═══════════════════════════════════════════════════════════════ */}
								<Row className="mt-3">
									{/* Market first, so the instrument list below is a short one. Hidden
									    while only one market is switched on, which is how it ships. */}
									{symbolStatus === "loaded" && marketOptions.length > 1 && (
										<Col md={3} sm={12}>
											<Form.Label>Market</Form.Label>
											<Form.Select
												name='market'
												value={activeMarket || ''}
												onChange={(e) => setChosenMarket(e.target.value)}
											>
												{marketOptions.map((m) => (
													<option key={m.id} value={m.id}>{m.label} ({m.count})</option>
												))}
												<option value={ALL_MARKETS}>All markets</option>
											</Form.Select>
											<Form.Text className="text-muted">
												{isMultiSymbolMode(selectedDevice)
													? 'Pick four from any markets. Switching market keeps what you have chosen.'
													: 'Sets what the instrument list offers.'}
											</Form.Text>
										</Col>
									)}

									{/* Single instrument (screens 1-3 and 5-8) */}
									{!isMultiSymbolMode(selectedDevice) && symbolStatus === "loaded" && (
										<Col md={isChartScreen(selectedDevice.screen) ? 3 : 6} sm={12}>
											<Form.Label>Instrument</Form.Label>
											<Select
												options={instrumentOptions}
												styles={instrumentSelectStyles}
												value={selectedInstrument || null}
												onChange={selectSymbol}
												placeholder={isGrouped(instrumentGroups) ? 'Choose a coin, pair or metal' : 'Choose a coin'}
											/>
											{instrumentMissing && (
												<Form.Text className="text-warning">
													Set to {selectedDevice.symbol}, which is not on offer right now.
													The device keeps showing it. Pick something else to change it.
												</Form.Text>
											)}
										</Col>
									)}

									{/* Four instruments (screen 4) */}
									{isMultiSymbolMode(selectedDevice) && symbolStatus === "loaded" && (
										<Col md={6} sm={12}>
											<Form.Label>Instruments (select exactly 4)</Form.Label>
											<Select
												isMulti
												options={instrumentOptions}
												styles={instrumentSelectStyles}
												value={findInstrumentOptions(instrumentGroups, selectedDevice.symbols)}
												onChange={selectMultipleSymbols}
												closeMenuOnSelect={false}
											/>
											<Form.Text className="text-muted">
												Selected: {selectedDevice.symbols?.length || 0}/4
											</Form.Text>
										</Col>
									)}

									{/* Price currency: half width beside the symbol picker, quarter width on chart screens */}
									<Col md={isChartScreen(selectedDevice.screen) ? 3 : 6} sm={12}>
										<Form.Label>Price currency</Form.Label>
										<Form.Select
											name='primaryCurrency'
											value={selectedDevice?.primaryCurrency || 'USD'}
											onChange={(e) => setSelectedDevice({ ...selectedDevice, primaryCurrency: e.target.value })}
										>
											<option value='USD'>US Dollar ($)</option>
											<option value='AUD'>Australian Dollar (A$)</option>
										</Form.Select>
										<Form.Text className="text-muted">Applies to the price, 24h stats and charts.</Form.Text>
									</Col>

									{/* Interval Selector (Chart screens 5-8 only) */}
									{isChartScreen(selectedDevice.screen) && (
										<Col md={3} sm={12}>
											<Form.Label>Interval</Form.Label>
											<Select
												options={['1m','5m','15m','1h','4h','1d','1w'].map((interval) => ({
													label: interval,
													value: interval
												}))}
												value={{ label: selectedDevice.interval, value: selectedDevice.interval }}
												onChange={(e) => setSelectedDevice({ ...selectedDevice, interval: e.value })}
											/>
										</Col>
									)}

									{/* Zoom/Limit Slider (Chart screens 5-8 only) */}
									{isChartScreen(selectedDevice.screen) && (
										<Col md={3} sm={12}>
											<Form.Label>Zoom (limit): {selectedDevice?.limit}</Form.Label>
											<Form.Range
												onChange={(e) => setSelectedDevice({ ...selectedDevice, limit: parseInt(e.target.value) })}
												value={selectedDevice?.limit || 50}
												name='limit'
												min="50"
												max="250"
											/>
										</Col>
									)}
								</Row>

								{/* Mode & Brightness */}
								<Row className="mt-3">
									<Col md={6} sm={12}>
										<Form.Label>Mode</Form.Label>
										<Row>
											<Col md={4}>
												<Form.Group 
													onClick={() => setSelectedDevice({ ...selectedDevice, mode: 'CryptoX' })}
													className={`form-group with-icon avatar ${selectedDevice.mode === 'CryptoX' ? 'selected' : ''}`}
												>
													<div className='cryptox avatar'></div>
													<Form.Label>CryptoX</Form.Label>
												</Form.Group>
											</Col>
											<Col md={4}>
												<Form.Group 
													onClick={() => setSelectedDevice({ ...selectedDevice, mode: 'Light' })}
													className={`form-group with-icon avatar ${selectedDevice.mode === 'Light' ? 'selected' : ''}`}
												>
													<div className='light avatar'></div>
													<Form.Label>Light</Form.Label>
												</Form.Group>
											</Col>
											<Col md={4}>
												<Form.Group 
													onClick={() => setSelectedDevice({ ...selectedDevice, mode: 'Dark' })}
													className={`form-group with-icon avatar ${selectedDevice.mode === 'Dark' ? 'selected' : ''}`}
												>
													<div className='dark avatar'></div>
													<Form.Label>Dark</Form.Label>
												</Form.Group>
											</Col>
										</Row>
									</Col>
									<Col md={6} sm={12}>
										<Form.Label>Brightness: {selectedDevice?.brightness}%</Form.Label>
										<Form.Range 
											onChange={(e) => setSelectedDevice({ ...selectedDevice, brightness: parseInt(e.target.value) })}
											value={selectedDevice?.brightness || 80}
											name='brightness'
											min="0"
											max="100"
										/>
									</Col>
								</Row>

								{/* Action Buttons */}
								<Row className='align-items-center justify-content-center mt-4'>
									<Col xl={6} lg={3} md={12} className="text-left text-md-right align-items-center d-flex d-lg-block justify-content-between">
										<Button className="btn-sm" variant="secondary" onClick={() => navigate('/devices')}>
											Cancel
										</Button>
										<Button className='btn-sm d-block d-lg-none' variant="primary" onClick={submitUpdateForm}>
											Update
										</Button>
									</Col>
									<Col xl={6} lg={9} md={12} className="text-start text-md-end text-sm-center d-flex d-lg-block justify-content-center justify-content-md-end flex-wrap align-items-sm-center">
										{selectedDevice.isPaidSubscription && selectedDevice.paymentStatus === 'Paid' && (
											<Button className='btn-sm' variant="cancel" onClick={handleShow}>
												Cancel Subscription
											</Button>
										)}
										<Button className='btn-sm d-none d-lg-inline-block' variant="primary" onClick={submitUpdateForm}>
											Update
										</Button>
									</Col>
								</Row>

								{/* Danger zone — separated from main controls so a fast-flicked
								    click on Update can't land here. Confirms by retyping the device_id. */}
								<Row className='mt-5'>
									<Col md={12}>
										<div style={{
											padding: '18px 22px',
											background: 'rgba(239, 68, 68, 0.06)',
											border: '1px solid rgba(239, 68, 68, 0.35)',
											borderRadius: 12,
											display: 'flex',
											flexWrap: 'wrap',
											gap: 16,
											alignItems: 'center',
											justifyContent: 'space-between',
										}}>
											<div>
												<div style={{ fontWeight: 600, color: '#FCA5A5', marginBottom: 2 }}>
													Delete this device
												</div>
												<div style={{ fontSize: 13, color: '#9CA3AF' }}>
													Removes the device from your account and cancels any active subscription.
													The physical screen will stop loading until it is registered again.
												</div>
											</div>
											<Button variant="outline-danger" size="sm" onClick={openDeleteModal}>
												Delete device…
											</Button>
										</div>
									</Col>
								</Row>
							</Container>

							{/* Cancel Subscription Modal */}
							<Modal show={showCancelSubscription} onHide={handleClose} size="md" fullscreen={'lg-down'}>
								<Modal.Header closeButton>
									<Modal.Title>Confirm</Modal.Title>
								</Modal.Header>
								<Modal.Body>
									<Row>
										<Col>
											<Form.Label>
												Please confirm if you would like to proceed with the cancellation of your subscription.
											</Form.Label>
										</Col>
									</Row>
								</Modal.Body>
								<Modal.Footer>
									<Button className='btn-sm' variant="secondary" onClick={handleClose}>Cancel</Button>
									<Button className='btn-sm' variant="primary" onClick={cancelSubscription}>Confirm</Button>
								</Modal.Footer>
							</Modal>

							{/* Delete-device confirmation: requires retyping the device_id */}
							<Modal show={showDeleteModal} onHide={closeDeleteModal} size="md" backdrop="static" keyboard={!deletePending}>
								<Modal.Header closeButton={!deletePending}>
									<Modal.Title>Delete device</Modal.Title>
								</Modal.Header>
								<Modal.Body>
									<p style={{ marginBottom: 12 }}>
										This will <strong>permanently remove</strong> <em>{selectedDevice?.name}</em> from your account.
										{selectedDevice?.isPaidSubscription && ' Any active subscription will be cancelled.'}
										The physical screen will stop loading until it is registered again.
									</p>
									<Form.Label>
										Type the device ID <code>{selectedDevice?.device_id}</code> to confirm:
									</Form.Label>
									<Form.Control
										type="text"
										value={deleteConfirmText}
										onChange={(e) => setDeleteConfirmText(e.target.value)}
										placeholder="device ID"
										disabled={deletePending}
										autoFocus
									/>
								</Modal.Body>
								<Modal.Footer>
									<Button variant="secondary" onClick={closeDeleteModal} disabled={deletePending}>Cancel</Button>
									<Button
										variant="danger"
										disabled={deletePending || deleteConfirmText !== selectedDevice?.device_id}
										onClick={confirmDelete}
									>
										{deletePending ? 'Deleting…' : 'Delete device'}
									</Button>
								</Modal.Footer>
							</Modal>

							{/* Upgrade Subscription Modal */}
							<Modal show={showUpgradeSubscription} onHide={handleUpgradeClose} size="xl" fullscreen={'lg-down'}>
								<Modal.Header closeButton>
									<Modal.Title>Payment</Modal.Title>
								</Modal.Header>
								<Modal.Body className='form-account'>
									<Row className='align-items-center pricing'>
										<Col className='each'>
											<label>
												<h2>$15.00/mo</h2>
												<ul>
													<li>Enjoy real-time data refreshes with up to 15-second latency for the most current information.</li>
												</ul>
												<input type="radio" value={'Monthly'} className="hidden" name="subscriptionType" />
											</label>
											<span>You will be registered in this subscription.</span>
										</Col>
										<Col>
											<Elements stripe={stripePromise}>
												<CheckoutForm submitSubscribe={submitSubscribe}/>
											</Elements>
										</Col>
									</Row>
								</Modal.Body>
								<Modal.Footer>
									<Button className='btn-sm' variant="secondary" onClick={handleUpgradeClose}>Cancel</Button>
								</Modal.Footer>
							</Modal>
						</>
					);
				}

				if (status === "loading") {
					return (
						<Container fluid className="element-page update-device h-100 pt-5 pb-5">
							<Row>
								<Col lg={12}>Loading...</Col>
							</Row>
						</Container>
					);
				}

				if (status === "error") {
					return (
						<Container fluid className="element-page update-device h-100 pt-5 pb-5">
							<Row>
								<Col md={12} className='text-center'>
									No Device Found <a className='link' onClick={() => navigate('/devices')}>Go back</a>
								</Col>
							</Row>
						</Container>
					);
				}
			})()}

			<Modal show={showClaimModal} onHide={closeClaimModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Pair Your Device</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>
						Open the <strong>CryptoX Manager</strong> mobile app, connect to your device,
						and enter this claim code on the WiFi setup screen.
					</p>
					<div className="text-center my-4 p-4" style={{
						background: '#1F1F35',
						borderRadius: '12px',
						border: '2px solid #7C3AED',
					}}>
						{claimStatus === 'loading' ? (
							<div style={{ color: '#9CA3AF' }}>Generating…</div>
						) : claim?.code ? (
							<>
								<div style={{
									fontFamily: 'monospace',
									fontSize: '2.5rem',
									letterSpacing: '0.5rem',
									fontWeight: 'bold',
									color: '#fff',
								}}>
									{claim.code}
								</div>
								<div className="mt-2" style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
									{formatExpiry(claim.expiresAt)}
								</div>
							</>
						) : (
							<div style={{ color: '#EF4444' }}>Could not generate code</div>
						)}
					</div>
					<p style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
						Codes are one-time use and expire in 15 minutes. Generate a new one any time.
					</p>
				</Modal.Body>
				<Modal.Footer>
					{claim?.code && (
						<Button variant="secondary" onClick={copyClaim}>
							{claimCopied ? 'Copied!' : 'Copy code'}
						</Button>
					)}
					<Button variant="primary" onClick={closeClaimModal}>Done</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
}